import { Suspense, lazy } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router";
import { RequireAuth } from "@/auth/RequireAuth";
import { DeployRefresh } from "@/components/DeployRefresh";
import { PageLoading } from "@/components/States";

const LoginPage = lazy(() => import("@/pages/LoginPage"));
const RegisterPage = lazy(() => import("@/pages/RegisterPage"));
const ForgotPasswordPage = lazy(() => import("@/pages/ForgotPasswordPage"));
const ResetPasswordPage = lazy(() => import("@/pages/ResetPasswordPage"));
const VerifyEmailPage = lazy(() => import("@/pages/VerifyEmailPage"));
const InvitePage = lazy(() => import("@/pages/InvitePage"));
const ShareAcceptPage = lazy(() => import("@/pages/ShareAcceptPage"));
const AppLayout = lazy(() => import("@/components/AppLayout"));
const AppHomePage = lazy(() => import("@/pages/AppHomePage"));
const PageViewPage = lazy(() => import("@/pages/PageViewPage"));
// PKG-AI (M3-03): project home projection.
const ProjectHomePage = lazy(() => import("@/pages/ProjectHomePage"));
// CORE-4: initiative detail (ordered projects + live rollup).
const InitiativeDetailPage = lazy(() => import("@/pages/InitiativeDetailPage"));
const TasksPage = lazy(() => import("@/pages/TasksPage"));
const TaskDetailPage = lazy(() => import("@/pages/TaskDetailPage"));
// PKG-AX (M5-01): dashboards — charts, map and feed widgets.
const DashboardsPage = lazy(() => import("@/pages/DashboardsPage"));
// CORE-107: the Factory dashboard — work metrics + coverage map from
// the read-only factory metric system sources.
const FactoryPage = lazy(() => import("@/pages/FactoryPage"));
// CORE-124: the operator AI-spend view — org-level COGS, top
// workspaces, model mix and the platform-vs-BYOK split, from the
// org-admin summary surface (managers are refused server-side).
const OperatorAiSpendPage = lazy(() => import("@/pages/OperatorAiSpendPage"));
// CORE-272: the operator traces export console — consent state,
// export preview + download, near-dup marking and the run list.
const OperatorTracesPage = lazy(() => import("@/pages/OperatorTracesPage"));
const OperatorKernelMonitorsPage = lazy(
  () => import("@/pages/OperatorKernelMonitorsPage"),
);
// CORE-167: Settings -> MCP analytics — org-level agent tool-call
// telemetry (per-tool/per-connection usage + recent failures with the
// Workers-Logs request id), org-admin only, from the internal read.
const McpAnalyticsPage = lazy(() => import("@/pages/McpAnalyticsPage"));
const InboxPage = lazy(() => import("@/pages/InboxPage"));
const SearchPage = lazy(() => import("@/pages/SearchPage"));
const AgentSettingsPage = lazy(() => import("@/pages/AgentSettingsPage"));
// CORE-52/74: settings -> integrations -> GitHub connector management.
const ConnectorSettingsPage = lazy(
  () => import("@/pages/ConnectorSettingsPage"),
);
// CORE-125: settings -> integrations -> ingest webhook endpoints + mappings.
const IntegrationsSettingsPage = lazy(
  () => import("@/pages/IntegrationsSettingsPage"),
);
const WorkTypeSettingsPage = lazy(() => import("@/pages/WorkTypeSettingsPage"));
const ReviewPolicySettingsPage = lazy(
  () => import("@/pages/ReviewPolicySettingsPage"),
);
// CORE-10: project reference prefixes (task key scheme) settings.
const ProjectSettingsPage = lazy(() => import("@/pages/ProjectSettingsPage"));
// CORE-260: the live agent console — the project's worker roster and
// streaming run log (the WATCHING half of the runtime UI).
const RuntimeConsolePage = lazy(() => import("@/pages/RuntimeConsolePage"));
// CORE-263: the runtime settings surface — bindings, profile, the
// deny-wins egress editor and budgets (the MANAGING half).
const RuntimeSettingsPage = lazy(() => import("@/pages/RuntimeSettingsPage"));
// CORE-292: the hosted org skills repo (Cloudflare Artifacts) —
// provisioning, the token ledger and the in-app SKILL.md editor.
const OrgSkillsRepoSettingsPage = lazy(
  () => import("@/pages/OrgSkillsRepoSettingsPage"),
);
// CORE-271: the org skills library — binding + Pi config editors and
// the per-project resolution inspector.
const SkillsLibrarySettingsPage = lazy(
  () => import("@/pages/SkillsLibrarySettingsPage"),
);
// Self-service display name (mentionability) settings.
const ProfileSettingsPage = lazy(() => import("@/pages/ProfileSettingsPage"));
// PKG-AD (M3-02): notifications surface + preferences.
const NotificationsPage = lazy(() => import("@/pages/NotificationsPage"));
const NotificationSettingsPage = lazy(
  () => import("@/pages/NotificationSettingsPage"),
);
const SharingSettingsPage = lazy(() => import("@/pages/SharingSettingsPage"));
// CORE-70: settings hub — the drawer's ONE Settings entry lands here; the
// settings/* deep links below keep working.
const SettingsHubPage = lazy(() => import("@/pages/SettingsHubPage"));
const ReleasesPage = lazy(() => import("@/pages/ReleasesPage"));
const TrashPage = lazy(() => import("@/pages/TrashPage"));
// CORE-11: standalone data sources (workspace home + detail).
const DataSourcesPage = lazy(() => import("@/pages/DataSourcesPage"));
const DataSourceDetailPage = lazy(() => import("@/pages/DataSourceDetailPage"));
// CORE-33: import review flow (batches + per-CSV type-mapping review).
const ImportsPage = lazy(() => import("@/pages/ImportsPage"));
const ImportBatchPage = lazy(() => import("@/pages/ImportBatchPage"));
const ImportMappingReviewPage = lazy(
  () => import("@/pages/ImportMappingReviewPage"),
);

// Dev/e2e-only styleguide catalog. Compiled out of production builds (the
// gate constant is `false` there, so Rollup drops the dynamic import).
const StyleguidePage = __CORENOTE_STYLEGUIDE__
  ? lazy(() => import("@/pages/StyleguidePage"))
  : null;

export default function App() {
  const location = useLocation();
  return (
    <>
      {/* Deploy refresh notice lives outside the routes so it survives
          navigation; it only appears when the served bundle changed. */}
      <DeployRefresh />
      <Routes location={location}>
        <Route
          path="/login"
          element={
            <Suspense fallback={<PageLoading label="Loading sign in…" />}>
              <LoginPage />
            </Suspense>
          }
        />
        <Route
          path="/register"
          element={
            <Suspense fallback={<PageLoading label="Loading registration…" />}>
              <RegisterPage />
            </Suspense>
          }
        />
        {/* PKG-J: emailed-link flows. All three are public routes — the token
          in the URL (or its absence) is the only credential. */}
        <Route
          path="/forgot-password"
          element={
            <Suspense
              fallback={<PageLoading label="Loading password reset…" />}
            >
              <ForgotPasswordPage />
            </Suspense>
          }
        />
        <Route
          path="/reset-password"
          element={
            <Suspense
              fallback={<PageLoading label="Loading password reset…" />}
            >
              <ResetPasswordPage />
            </Suspense>
          }
        />
        <Route
          path="/verify-email"
          element={
            <Suspense fallback={<PageLoading label="Verifying your email…" />}>
              <VerifyEmailPage />
            </Suspense>
          }
        />
        <Route
          path="/invite/:token"
          element={
            <Suspense fallback={<PageLoading label="Loading invitation…" />}>
              <InvitePage />
            </Suspense>
          }
        />
        {/* PKG-AO (M3-03): emailed share link. Public route — the token is the
          credential; the page itself routes through sign-in when no
          verified matching session exists. */}
        <Route
          path="/share/:token"
          element={
            <Suspense fallback={<PageLoading label="Loading shared page…" />}>
              <ShareAcceptPage />
            </Suspense>
          }
        />
        <Route
          path="/share"
          element={
            <Suspense fallback={<PageLoading label="Loading shared page…" />}>
              <ShareAcceptPage />
            </Suspense>
          }
        />
        <Route
          path="/app"
          element={
            <RequireAuth>
              <Suspense fallback={<PageLoading label="Loading workspace…" />}>
                <AppLayout />
              </Suspense>
            </RequireAuth>
          }
        >
          <Route
            index
            element={
              <Suspense fallback={<PageLoading label="Loading home…" />}>
                <AppHomePage />
              </Suspense>
            }
          />
          <Route
            path="pages/:pageId"
            element={
              <Suspense fallback={<PageLoading label="Loading page…" />}>
                <PageViewPage />
              </Suspense>
            }
          />
          <Route
            path="pages/:pageId/project"
            element={
              <Suspense
                fallback={<PageLoading label="Loading project home…" />}
              >
                <ProjectHomePage />
              </Suspense>
            }
          />
          {/* CORE-260: the live agent console — worker roster +
              streaming run log for one project (runtime spike flag
              gates the API surface; the page renders the 404 refusal
              honestly when the flag is off). */}
          <Route
            path="runtime/:projectId"
            element={
              <Suspense fallback={<PageLoading label="Loading runtime…" />}>
                <RuntimeConsolePage />
              </Suspense>
            }
          />
          {/* CORE-263: the runtime settings surface — bindings, service
              profile, the deny-wins egress editor and budgets with
              live spend (Admin-admitted config ops; honest refusal
              states otherwise). */}
          <Route
            path="runtime/:projectId/settings"
            element={
              <Suspense fallback={<PageLoading label="Loading runtime…" />}>
                <RuntimeSettingsPage />
              </Suspense>
            }
          />
          {/* CORE-4: initiative detail above the project surfaces. */}
          <Route
            path="initiatives/:initiativeId"
            element={
              <Suspense fallback={<PageLoading label="Loading initiative…" />}>
                <InitiativeDetailPage />
              </Suspense>
            }
          />
          <Route
            path="tasks"
            element={
              <Suspense fallback={<PageLoading label="Loading tasks…" />}>
                <TasksPage />
              </Suspense>
            }
          />
          <Route
            path="tasks/:taskId"
            element={
              <Suspense fallback={<PageLoading label="Loading task…" />}>
                <TaskDetailPage />
              </Suspense>
            }
          />
          {/* CORE-11: standalone data sources live at the workspace level. */}
          <Route
            path="data-sources"
            element={
              <Suspense
                fallback={<PageLoading label="Loading data sources…" />}
              >
                <DataSourcesPage />
              </Suspense>
            }
          />
          <Route
            path="data-sources/:sourceId"
            element={
              <Suspense fallback={<PageLoading label="Loading data source…" />}>
                <DataSourceDetailPage />
              </Suspense>
            }
          />
          {/* CORE-33: the import review flow — intake lands a batch here,
              database CSVs get their mapping reviewed before any run. */}
          <Route
            path="imports"
            element={
              <Suspense fallback={<PageLoading label="Loading imports…" />}>
                <ImportsPage />
              </Suspense>
            }
          />
          <Route
            path="imports/:batchId"
            element={
              <Suspense
                fallback={<PageLoading label="Loading import batch…" />}
              >
                <ImportBatchPage />
              </Suspense>
            }
          />
          <Route
            path="imports/:batchId/review/:entryId"
            element={
              <Suspense
                fallback={<PageLoading label="Loading mapping review…" />}
              >
                <ImportMappingReviewPage />
              </Suspense>
            }
          />
          <Route
            path="dashboards"
            element={
              <Suspense fallback={<PageLoading label="Loading dashboards…" />}>
                <DashboardsPage />
              </Suspense>
            }
          />
          <Route
            path="factory"
            element={
              <Suspense fallback={<PageLoading label="Loading factory…" />}>
                <FactoryPage />
              </Suspense>
            }
          />
          <Route
            path="operator/ai-spend"
            element={
              <Suspense fallback={<PageLoading label="Loading AI spend…" />}>
                <OperatorAiSpendPage />
              </Suspense>
            }
          />
          <Route
            path="operator/traces"
            element={
              <Suspense
                fallback={<PageLoading label="Loading traces export…" />}
              >
                <OperatorTracesPage />
              </Suspense>
            }
          />
          <Route
            path="operator/kernel-monitors"
            element={
              <Suspense
                fallback={<PageLoading label="Loading kernel monitors…" />}
              >
                <OperatorKernelMonitorsPage />
              </Suspense>
            }
          />
          <Route
            path="settings/mcp-analytics"
            element={
              <Suspense
                fallback={<PageLoading label="Loading MCP analytics…" />}
              >
                <McpAnalyticsPage />
              </Suspense>
            }
          />
          <Route
            path="releases"
            element={
              <Suspense fallback={<PageLoading label="Loading releases…" />}>
                <ReleasesPage />
              </Suspense>
            }
          />
          <Route
            path="trash"
            element={
              <Suspense fallback={<PageLoading label="Loading trash…" />}>
                <TrashPage />
              </Suspense>
            }
          />
          <Route
            path="inbox"
            element={
              <Suspense fallback={<PageLoading label="Loading inbox…" />}>
                <InboxPage />
              </Suspense>
            }
          />
          <Route
            path="search"
            element={
              <Suspense fallback={<PageLoading label="Loading search…" />}>
                <SearchPage />
              </Suspense>
            }
          />
          {/* CORE-70: the hub indexes the settings/* sections below. */}
          <Route
            path="settings"
            element={
              <Suspense fallback={<PageLoading label="Loading settings…" />}>
                <SettingsHubPage />
              </Suspense>
            }
          />
          <Route
            path="settings/agents"
            element={
              <Suspense
                fallback={<PageLoading label="Loading agent settings…" />}
              >
                <AgentSettingsPage />
              </Suspense>
            }
          />
          {/* CORE-52/74 + CORE-240: connectors (GitHub v1, Linear import) —
              manager/admin only. */}
          <Route
            path="settings/integrations/github"
            element={
              <Suspense fallback={<PageLoading label="Loading connectors…" />}>
                <ConnectorSettingsPage />
              </Suspense>
            }
          />
          {/* CORE-125: vendor-neutral ingest webhook endpoints — manager/admin
              only; the public ingest route itself is unauthenticated. */}
          <Route
            path="settings/integrations/webhooks"
            element={
              <Suspense
                fallback={<PageLoading label="Loading webhook integrations…" />}
              >
                <IntegrationsSettingsPage />
              </Suspense>
            }
          />
          {/* CORE-292: the hosted org skills repo — org-admin provisioning,
              the one-time token ledger and the in-app git editor. */}
          <Route
            path="settings/skills-repo"
            element={
              <Suspense
                fallback={<PageLoading label="Loading skills repository…" />}
              >
                <OrgSkillsRepoSettingsPage />
              </Suspense>
            }
          />
          {/* CORE-271: the org skills library — binding + Pi config
              editors and the per-project resolution inspector (org
              admin writes; member reads). */}
          <Route
            path="settings/skills"
            element={
              <Suspense
                fallback={<PageLoading label="Loading skills library…" />}
              >
                <SkillsLibrarySettingsPage />
              </Suspense>
            }
          />
          <Route
            path="settings/profile"
            element={
              <Suspense fallback={<PageLoading label="Loading profile…" />}>
                <ProfileSettingsPage />
              </Suspense>
            }
          />
          <Route
            path="settings/work-types"
            element={
              <Suspense fallback={<PageLoading label="Loading work types…" />}>
                <WorkTypeSettingsPage />
              </Suspense>
            }
          />
          {/* CORE-105: risk-tiered review policy + run-identity usage. */}
          <Route
            path="settings/review-policy"
            element={
              <Suspense
                fallback={<PageLoading label="Loading review policy…" />}
              >
                <ReviewPolicySettingsPage />
              </Suspense>
            }
          />
          {/* CORE-10: per-project task reference prefixes. */}
          <Route
            path="settings/projects"
            element={
              <Suspense fallback={<PageLoading label="Loading projects…" />}>
                <ProjectSettingsPage />
              </Suspense>
            }
          />
          {/* PKG-AD (M3-02): mentions/notifications + preference settings. */}
          <Route
            path="notifications"
            element={
              <Suspense
                fallback={<PageLoading label="Loading notifications…" />}
              >
                <NotificationsPage />
              </Suspense>
            }
          />
          <Route
            path="settings/notifications"
            element={
              <Suspense
                fallback={
                  <PageLoading label="Loading notification settings…" />
                }
              >
                <NotificationSettingsPage />
              </Suspense>
            }
          />
          <Route
            path="settings/sharing"
            element={
              <Suspense fallback={<PageLoading label="Loading sharing…" />}>
                <SharingSettingsPage />
              </Suspense>
            }
          />
        </Route>
        <Route path="/" element={<Navigate to="/app" replace />} />
        <Route path="*" element={<Navigate to="/app" replace />} />
        {StyleguidePage ? (
          <Route
            path="/dev/styleguide"
            element={
              <Suspense fallback={<PageLoading label="Loading styleguide…" />}>
                <StyleguidePage />
              </Suspense>
            }
          />
        ) : null}
      </Routes>
    </>
  );
}
